import { FC } from 'react'
import { RouteAlternative } from '../../api/tollRoutes.api'

interface TollRouteControlsProps {
    tollRoutes: RouteAlternative[]
    selectedRouteIndex: number
    isTollLoading: boolean
    showTollBoxes: boolean
    tollError: string | null
    origin: { city: string; state: string }
    destination: { city: string; state: string }
    onGetTolls: (e: React.MouseEvent) => void
    onTollBoxClick: (index: number) => void
    onHideTolls: () => void
}

export const TollRouteControls: FC<TollRouteControlsProps> = ({
    tollRoutes,
    selectedRouteIndex,
    isTollLoading,
    showTollBoxes,
    tollError,
    origin,
    destination,
    onGetTolls,
    onTollBoxClick,
    onHideTolls,
}) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${origin.city}, ${origin.state}`)}&destination=${encodeURIComponent(`${destination.city}, ${destination.state}`)}&travelmode=driving`

    return (
        <>
            {/* Get Tolls Button */}
            {!showTollBoxes && (
                <button
                    onClick={onGetTolls}
                    disabled={isTollLoading}
                    className="absolute top-3 right-3 z-[500] bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-medium px-3 py-1.5 rounded shadow-sm flex items-center gap-1.5"
                >
                    {isTollLoading ? (
                        <>
                            <svg className="h-2.5 w-2.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <span>Loading...</span>
                        </>
                    ) : (
                        'Get Tolls'
                    )}
                </button>
            )}

            {/* Toll Action Buttons */}
            {showTollBoxes && (
                <div className="absolute top-3 right-3 flex gap-2 z-[500]">
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            window.open(googleMapsUrl, '_blank')
                        }}
                        // Matches the dark treatment of Leaflet's own controls (`.dark .leaflet-bar`
                        // in styles/index.css) so it doesn't pop as a bright chip over the dark basemap.
                        className="bg-white hover:bg-gray-50 text-gray-600 text-[11px] font-medium px-2.5 py-1.5 rounded shadow-sm border border-gray-200 flex items-center gap-1 dark:bg-[var(--lh-surface)] dark:hover:bg-[var(--lh-surface-secondary)] dark:text-[var(--lh-fg)] dark:border-[var(--lh-border)]"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Details
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            onHideTolls()
                        }}
                        className="bg-sky-500 hover:bg-sky-600 text-white text-[11px] font-medium px-2.5 py-1.5 rounded shadow-sm"
                    >
                        Hide
                    </button>
                </div>
            )}

            {/* Toll Cost Boxes */}
            {showTollBoxes && tollRoutes.length > 0 && (
                <div className="absolute bottom-2 left-2 flex gap-1 overflow-x-auto z-[500]">
                    {tollRoutes.slice(0, 4).map((route, index) => {
                        const isSelected = index === selectedRouteIndex
                        const tollAmount = route.tollCost?.amount || 0
                        return (
                            <button
                                key={route.routeId}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onTollBoxClick(index)
                                }}
                                className={`flex-shrink-0 px-1.5 py-0.5 rounded text-center transition-all shadow-sm cursor-pointer ${
                                    isSelected ? 'bg-sky-600 text-white ring-1 ring-white/50' : 'bg-sky-500/90 text-white hover:bg-sky-600'
                                }`}
                            >
                                <div className="text-[10px] font-semibold leading-tight">
                                    ${(typeof tollAmount === 'number' && !isNaN(tollAmount) ? tollAmount : 0).toFixed(2)}
                                </div>
                                <div className="text-[8px] opacity-75 leading-tight">{Math.round(route.distance?.miles || 0)} mi</div>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Error Message */}
            {tollError && <div className="absolute top-12 right-3 bg-red-50 text-red-600 text-[10px] px-2 py-1 rounded shadow z-[500]">{tollError}</div>}
        </>
    )
}
